<script lang="ts">
	import SbRichtext from '../components/sbRichtext.svelte';
	import PageGrid from '../components/pageGrid.svelte';
	import EmailCaptureInput, { type FormState } from '$lib/emailCaptureInput.svelte';
	import { getSbUrl } from '$lib/utils/storyblok/url';
	import type { SbLink } from '../types';
	import type { ChewieUMCouponIdentifier } from '$lib/api/chewieUM.types';
	import Button from '$lib/buttons/v3/button.svelte';
	import type { ISbRichtext, ISbStoryData } from '@storyblok/js';
	import { editable } from '../editable';
	import Blok from '../blok.svelte';
	import { storyblok } from '$lib/state/storyblok.svelte';
	import log from '$lib/utils/log';
	import { onMount } from 'svelte';

	interface Props {
		content: ISbStoryData<{
			asset: ISbStoryData[];
			headline: string;
			text: ISbRichtext;
			legal_hint: ISbRichtext;
			cta_title: string;
			success_headline: string;
			success_text: ISbRichtext;
			success_cta_title: string;
			success_cta_link: SbLink;
			coupon_identifier: ChewieUMCouponIdentifier;
			coupon_text: string;
			template: string;
			use_template_headline?: boolean;
			use_template_text?: boolean;
			use_template_coupon?: boolean;
			use_template_success_state?: boolean;
			use_template_asset?: boolean;
		}>['content'];
	}

	let { content }: Props = $props();

	let formState = $state<FormState>(null);

	async function mergeTemplate(uuid: string) {
		try {
			const { data } = await storyblok.client.getStories({
				starts_with: 'globals/email-capture-templates/',
				by_uuids: uuid
			});

			if (data.stories.length === 0) return null;

			const template = data.stories[0].content;

			mergedContent = {
				...content,
				...(content.use_template_asset && { asset: template.asset }),
				...(content.use_template_headline && { headline: template.headline }),
				...(content.use_template_text && { text: template.text }),
				...(content.use_template_coupon && {
					coupon_identifier: template.coupon_identifier,
					coupon_text: template.coupon_text
				}),
				...(content.use_template_success_state && {
					success_headline: template.success_headline,
					success_text: template.success_text,
					success_cta_link: template.success_cta_link,
					success_cta_title: template.success_cta_title
				})
			};
		} catch (err) {
			log.scope('emailCaptureEmbed').exception(err);
		}
	}

	let mounted = $state(false);
	let mergedContent = $state(content);

	onMount(async () => {
		if (content.template) {
			await mergeTemplate(content.template);
		}
		mounted = true;
	});
</script>

<section use:editable={content} class="bg-stone-4 pb-7 pt-4">
	<PageGrid>
		{#if mergedContent.asset?.length > 0}
			<div
				class={[
					'col-span-full mb-5 aspect-4/3 overflow-hidden rounded-xl bg-stone-3/50 lg:col-start-2 lg:col-end-7',
					{ 'animate-pulse': content.use_template_asset && !mounted }
				]}
			>
				<Blok content={mergedContent.asset[0]} />
			</div>
		{/if}

		<div
			class="col-span-full lg:col-start-7 lg:col-end-12 lg:flex lg:flex-col lg:justify-center lg:px-16"
		>
			<h1
				class={[
					'mb-3 text-[32px]/[35.2px] font-semibold tracking-[-.01] text-brown-2 lg:text-[40px]/[44px]',
					{
						'animate-pulse rounded-xl bg-stone-3/50 text-transparent':
							content.use_template_headline && !mounted
					}
				]}
			>
				<SbRichtext
					text={formState === 'success' ? mergedContent.success_headline : mergedContent.headline}
				/>
			</h1>

			<div
				class={[
					'mb-4 text-brown-2',
					{
						'animate-pulse rounded-xl bg-stone-3/50 text-transparent':
							content.use_template_text && !mounted
					}
				]}
			>
				<SbRichtext
					text={formState === 'success' ? mergedContent.success_text : mergedContent.text}
				/>
			</div>

			{#if formState === 'success'}
				<Button href={getSbUrl(mergedContent.success_cta_link)}
					>{mergedContent.success_cta_title}</Button
				>
			{:else}
				<EmailCaptureInput
					bind:state={formState}
					buttonLabel={mergedContent.cta_title}
					couponIdentifier={mergedContent.coupon_identifier}
					couponText={mergedContent.coupon_text}
				>
					{#snippet legal()}
						<SbRichtext text={mergedContent.legal_hint} />
					{/snippet}
				</EmailCaptureInput>
			{/if}
		</div>
	</PageGrid>
</section>
